import { api } from './client';

/** Specchio di TeraWorks.Application.Articoli / Abstractions.SchedaArticolo. */

export type OrigineCosto =
  | 'NettoDaListinoFornitore'
  | 'ScontoDaListinoFornitore'
  | 'ScontoDaGriglia'
  | 'UltimoAcquisto';

export interface OffertaConfrontata {
  id: string;
  codiceFornitoreMago: string;
  fornitore: string | null;
  codiceArticoloFornitore: string | null;
  descrizioneFornitore: string | null;
  costoNetto: number;
  prezzoListinoFornitore: number | null;
  scontoPercentuale: number;
  scontoTesto: string | null;
  origine: OrigineCosto;
  validaDal: string;
  validaAl: string | null;
  confezioneMinima: number | null;
  scontoSulListinoProduttore: number | null;
  differenzaDallaMigliore: number | null;
  differenzaPercentuale: number | null;
  migliore: boolean;
  motivoEsclusione: string | null;
  /*  Arriva da un altro articolo, che qualcuno ha confermato essere lo stesso. Si mostra
      perché è il primo posto da guardare quando un confronto sembra sbagliato: nasconderlo
      renderebbe l'abbinamento invisibile proprio a chi può accorgersi che è storto.  */
  daAbbinamento: boolean;
}

export interface RisultatoConfronto {
  confrontabili: OffertaConfrontata[];
  fuoriConfronto: OffertaConfrontata[];
  migliore: OffertaConfrontata | null;
  forbice: number | null;
}

export interface SchedaArticolo {
  id: string;
  siglaProduttore: string;
  produttore: string | null;
  codiceProduttore: string;
  codiceInterno: string | null;
  descrizione: string;
  unitaMisura: string | null;
  attivo: boolean;
  prezzoListino: number | null;
  listinoAggiornatoAl: string | null;
  confronto: RisultatoConfronto;
}

export const ETICHETTE_ORIGINE: Record<OrigineCosto, string> = {
  NettoDaListinoFornitore: 'netto da listino fornitore',
  ScontoDaListinoFornitore: 'sconto dal listino fornitore',
  ScontoDaGriglia: 'griglia sconti concordata',
  UltimoAcquisto: 'ultimo prezzo pagato',
};

/*  Gli abbinamenti: «questo articolo e quello sono la stessa cosa».

    Servono perché la chiave del confronto — sigla più codice del produttore — regge finché
    tutti scrivono lo stesso codice, e i grossisti non lo fanno. Qualcuno propone, una
    persona conferma, e da lì il confronto mette insieme le offerte dei due.

    La proposta porta con sé le PROVE — i due codici, le due descrizioni, i due costi —
    perché è su quelle che si decide: due articoli che dicono di essere lo stesso e costano
    uno il triplo dell'altro sono quasi sempre due articoli diversi.  */
export interface AbbinamentoLetto {
  id: string;
  /** Proposto, Confermato o Rifiutato. Solo i confermati allargano il confronto. */
  stato: string;
  /** Da 0 a 1. Ordina la coda, non decide: nessuna soglia fa passare niente da sola. */
  confidenza: number;
  motivazione: string;
  /** Chi l'ha proposto: il nome della regola o del modello. Serve a giudicarne una. */
  proponente: string;
  articoloId: string;
  codiceVariante: string;
  descrizioneVariante: string;
  fornitoreVariante: string;
  costoVariante: number | null;
  canonicoId: string;
  codiceCanonico: string;
  descrizioneCanonico: string;
  fornitoreCanonico: string;
  costoCanonico: number | null;
  siglaProduttore: string;
  creatoIl: string;
  decisoDa: string | null;
}

/** <param name="gia">Coppie già in coda o già decise: non si ripropongono.</param> */
export interface EsitoRicercaAbbinamenti {
  siglaProduttore: string;
  proponente: string;
  articoliGuardati: number;
  proposte: number;
  gia: number;
  messaggio: string | null;
}

/*  Cosa il modello può fare in questa installazione.

    `chiaveMascherata` sono le ultime quattro cifre o «assente»: rispondono a «è caricata
    quella giusta?» senza rispondere a «qual è?». La chiave intera non esce da nessuna
    rotta, ed è una regola e non un caso.  */
export interface ConfigurazioneIa {
  testoDisponibile: boolean;
  visioneDisponibile: boolean;
  modelloTesto: string | null;
  modelloVisione: string | null;
  chiaveMascherata: string;
  richiesteMassimePerLavoro: number;
  motivo: string | null;
}

/*  Un articolo come si legge scegliendolo, non come si legge studiandolo: cosa è, e quanto
    costa da chi ce l'ha meglio. La scheda intera — tutte le offerte, i listini, lo storico
    — sta un clic più in là, e portarsela dietro in un elenco di venticinque risultati
    vorrebbe dire venticinque confronti letti per sceglierne uno.  */
export interface ArticoloTrovato {
  id: string;
  siglaProduttore: string;
  produttore: string | null;
  codiceProduttore: string;
  codiceInterno: string | null;
  descrizione: string;
  unitaMisura: string | null;
  prezzoListino: number | null;
  migliorCosto: number | null;
  migliorFornitore: string | null;
  /** Quanti lo offrono: «22,58 da uno» e «22,58 su quattro» sono due situazioni diverse. */
  fornitoriConOfferta: number | null;
}

/*  L'esito, e non il solo elenco, perché i risultati della seconda passata vanno spiegati.

    Cercando «2430586» non si trova niente per descrizione o codice produttore: quello è il
    codice con cui ABBATTISTA chiama un lavabo Hatria, e sta sulle sue offerte. La seconda
    passata lo trova, ma il risultato — un lavabo che si chiama «BH0800465011TS0» — non
    somiglia per niente a quello che si è battuto, e senza una riga che lo dica sembra che
    la ricerca abbia risposto a caso.  */
export interface EsitoRicercaArticoli {
  trovati: ArticoloTrovato[];
  /** Vero quando questi sono usciti cercando anche nei codici dei fornitori. */
  colCodiceFornitore: boolean;
}

export const articoli = {
  scheda: (id: string) => api.get<SchedaArticolo>(`/articoli/${id}/confronto`),

  /*  Tutte le parole, in qualunque campo: «tubo rame 1/2» trova i raccordi per tubo di
      rame da mezzo pollice, non tutti i tubi più tutto il rame. Il più divide come lo
      spazio, la barra no.  */
  cerca: (frase: string, massimo = 25) =>
    api.get<EsitoRicercaArticoli>(
      `/articoli/cerca?frase=${encodeURIComponent(frase)}&massimo=${massimo}`,
    ),

  schedaPerChiave: (sigla: string, codice: string) =>
    api.get<SchedaArticolo>(
      `/articoli/confronto?sigla=${encodeURIComponent(sigla)}&codice=${encodeURIComponent(codice)}`,
    ),

  abbinamenti: {
    inAttesa: (quante = 100) =>
      api.get<AbbinamentoLetto[]>(`/articoli/abbinamenti?quante=${quante}`),

    /*  POST e non GET pur non decidendo niente: scrive delle proposte, e soprattutto è il
        punto in cui parte una chiamata a pagamento verso un modello. Una GET la farebbero
        i browser da soli.  */
    cerca: (sigla: string) =>
      api.post<EsitoRicercaAbbinamenti>(
        `/articoli/abbinamenti/ricerca/${encodeURIComponent(sigla)}`,
        {},
      ),

    conferma: (id: string) =>
      api.post<AbbinamentoLetto>(`/articoli/abbinamenti/${id}/conferma`, {}),

    rifiuta: (id: string) =>
      api.post<AbbinamentoLetto>(`/articoli/abbinamenti/${id}/rifiuto`, {}),
  },

  /** Cosa il modello può fare qui. La chiave non esce: solo le ultime quattro cifre. */
  configurazioneIa: () => api.get<ConfigurazioneIa>('/configurazione/ia'),
};
